import { useEffect, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import { Modal } from 'semantic-ui-react';

/**
 * ImagePickerModal
 *
 * Shows a grid of image documents uploaded to the activity's Documents & Media
 * card. The user selects one and confirms; the caller receives the selection.
 *
 * Props:
 *   open         {boolean}   Whether the modal is visible
 *   projectId    {number}    Activity (project) ID to load images for
 *   multiple     {boolean}   Allow selecting more than one image (default false)
 *   onSelect     {function}  Single mode: called with { documentId, url, name }.
 *                            Multiple mode: called with an array of the same.
 *   onClose      {function}  Called when the modal is dismissed without selection
 */
const ImagePickerModal = ({
  open,
  projectId,
  multiple = false,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // single mode: image | null
  const [selectedMulti, setSelectedMulti] = useState([]); // multiple mode: image[]

  // Fetch images whenever the modal opens
  useEffect(() => {
    if (!open || !projectId) return;
    setSelected(null);
    setSelectedMulti([]);
    setError(null);
    setLoading(true);
    axios
      .get(`/project/${projectId}/report-builder/images`)
      .then(({ data }) => {
        setImages(data.images || []);
      })
      .catch(() => setError(t('image_picker.could_not_load_images')))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const isSelected = (img) =>
    multiple
      ? selectedMulti.some((s) => s.documentId === img.documentId)
      : selected?.documentId === img.documentId;

  const toggleImage = (img) => {
    if (!multiple) {
      setSelected(img);
      return;
    }
    setSelectedMulti((prev) =>
      prev.some((s) => s.documentId === img.documentId)
        ? prev.filter((s) => s.documentId !== img.documentId)
        : [...prev, img]
    );
  };

  const handleConfirm = useCallback(() => {
    if (multiple) {
      if (selectedMulti.length) onSelect(selectedMulti);
    } else if (selected) {
      onSelect(selected);
    }
  }, [multiple, selected, selectedMulti, onSelect]);

  const canConfirm = multiple ? selectedMulti.length > 0 : !!selected;

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>{t('image_picker.select_image_from')}</Modal.Header>
      <Modal.Content scrolling>
        {loading && (
          <div className="img-picker__loading">
            {t('image_picker.loading_images')}…
          </div>
        )}
        {error && <div className="img-picker__error">{error}</div>}
        {!loading && !error && images.length === 0 && (
          <div className="img-picker__empty">
            <Trans i18nKey="image_picker.no_images_found_upload" />
          </div>
        )}
        {!loading && images.length > 0 && (
          <div className="img-picker__grid">
            {images.map((img) => (
              <button
                key={img.documentId}
                type="button"
                className={`img-picker__thumb${
                  isSelected(img) ? ' img-picker__thumb--selected' : ''
                }`}
                onClick={() => toggleImage(img)}
                title={img.name}
              >
                <img src={img.url} alt={img.name} />
                <span className="img-picker__thumb-label">{img.name}</span>
              </button>
            ))}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <button type="button" className="ui button" onClick={onClose}>
          {t('actions.cancel')}
        </button>
        <button
          type="button"
          className={`ui primary button${!canConfirm ? ' disabled' : ''}`}
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          {multiple
            ? t('image_picker.use_n_images', { count: selectedMulti.length })
            : t('image_picker.use_this_image')}
        </button>
      </Modal.Actions>
    </Modal>
  );
};

export default ImagePickerModal;
